import { useAppDispatch } from '@/store/hooks';
import { getNestedString, useConfig } from '@/utils/useConfig';
import { closeDialog, openDialog } from '@fuse/core/FuseDialog/fuseDialogSlice';
import FuseLoading from '@fuse/core/FuseLoading';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import React, { useEffect } from 'react';
import MarkdownRenderer from '../MarkdownRenderer';

/**
 * PrivacyLink Component
 * 
 * A clickable link component that opens a modal dialog displaying the privacy policy.
 * The privacy policy content is loaded from the application configuration and
 * rendered as markdown. The modal includes a close button and uses the Fuse dialog system.
 * 
 * @returns JSX element containing a clickable privacy policy link
 */
function PrivacyLink() {
	const dispatch = useAppDispatch();

	const { config, loading, error, refresh } = useConfig('privacy_config');

	useEffect(() => {
		refresh();
	}, [refresh]);

	/**
	 * Handles opening the privacy policy modal dialog.
	 * Creates a dialog with the privacy policy title and content from configuration.
	 */
	const handleOpenPrivacyDialog = async () => {

		await refresh();

		dispatch(
			openDialog({
				children: (
					<React.Fragment>
						{/* Dialog Header with Title and Close Button */}
						<DialogTitle
							id="alert-dialog-title"
							className="flex items-center justify-between"
						>
							Privacy Policy
							<IconButton
								onClick={() => dispatch(closeDialog())}
								color="error"
							>
								<FuseSvgIcon
									size={12}
									className="arrow-icon"
								>
									heroicons-outline:x-mark
								</FuseSvgIcon>
							</IconButton>
						</DialogTitle>

						<DialogContent>
							{
								loading ? <FuseLoading /> : error ? <p>{error}</p> : <MarkdownRenderer markdown={getNestedString(config, 'content')} />
							}
						</DialogContent>

					</React.Fragment>
				),
				options: {
					maxWidth: false,
					paperClassName: 'max-w-4xl w-full'
				}
			})
		);
	};

	return (
		<button
			type="button"
			className="underline text-primary "
			onClick={handleOpenPrivacyDialog}
		>
			Privacy Policy
		</button>
	);
}

export default PrivacyLink;
